/**
 * Apollo Client wiring behind `GraphqlProgrammePlatformClient`.
 *
 * Queries share one HTTP request when issued together (`BatchHttpLink`); the
 * server executes a batch in parallel, so mutations, whose order matters, each
 * go alone. Nothing is cached: every contract method promises a fresh read.
 * A failure the API answers leaves as {@link PlatformApiError}, whatever shape it
 * arrived in. A request that never reached the API keeps the error that
 * stopped it, `fetch`'s own or the access token's.
 *
 * @module platform/graphql-transport
 */

import {
  ApolloClient,
  ApolloLink,
  gql,
  HttpLink,
  InMemoryCache,
  ServerError,
  ServerParseError,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { SetContextLink } from "@apollo/client/link/context";
import { isMutationOperation, stripTypename } from "@apollo/client/utilities";
import {
  type DocumentNode,
  type FragmentDefinitionNode,
  getOperationAST,
  Kind,
  OperationTypeNode,
  type SelectionNode,
  type ValueNode,
} from "graphql";
import { catchError, map } from "rxjs";

import { type GraphqlWireError, PlatformApiError, wireErrors, wireStatus } from "./api-error";

/** The API's batch cap; a longer batch is refused whole. */
const MAX_BATCH = 20;

/**
 * Header naming the tenant the caller is acting in. Mirrors the API server's
 * `TENANT_HEADER`; a literal so the Fastify server stays out of the bundle.
 */
const TENANT_HEADER = "x-platform-tenant";

export interface GraphqlTransportOptions {
  endpoint: string;
  getAccessToken: () => Promise<string | null | undefined>;
  tenantId?: string;
  fetchImpl: typeof fetch;
  headers: HeadersInit;
}

function requestIdOf(response: Response | undefined): string | undefined {
  return response?.headers.get("x-request-id") ?? undefined;
}

function platformError(
  failure: GraphqlWireError,
  fallbackStatus: number,
  requestId: string | undefined,
): PlatformApiError {
  return new PlatformApiError(
    failure.message || `Platform API request failed (${String(fallbackStatus)})`,
    {
      status: wireStatus(failure) ?? fallbackStatus,
      code: failure.extensions?.code,
      details: failure.extensions?.details,
      requestId,
    },
  );
}

/** An answer leaving out a field asked for: a 502, since the server answered, but not with that. */
function incompleteAnswer(requestId: string | undefined): PlatformApiError {
  return new PlatformApiError("The API answered without a result.", {
    status: 502,
    code: "incomplete_response",
    requestId,
  });
}

/** The status of an answer the API refused with, or nothing for a 2xx. */
function refusedStatus(response: Response | undefined): number | undefined {
  return response !== undefined && response.status >= 300 ? response.status : undefined;
}

/** An answer whose body is no GraphQL response: a refusal keeps its status, a 2xx is a 502. */
function unreadableAnswer(response: Response | undefined): PlatformApiError {
  const requestId = requestIdOf(response);
  const refused = refusedStatus(response);
  if (refused !== undefined) {
    return platformError({}, refused, requestId);
  }
  return new PlatformApiError("The API answered with a body that is not a GraphQL response.", {
    status: 502,
    code: "unreadable_response",
    requestId,
  });
}

/**
 * The keys an answer to `document` holds its root fields under, those inside
 * fragments included, and those `@skip` or `@include` leave out under
 * `variables` excluded.
 */
function rootFieldsOf(document: DocumentNode, variables: Record<string, unknown>): string[] {
  const operation = getOperationAST(document);
  // Apollo has filled in each variable's default by the time a link sees them.
  const holds = (value: ValueNode | undefined) =>
    value?.kind === Kind.VARIABLE
      ? variables[value.name.value] === true
      : value?.kind === Kind.BOOLEAN && value.value;
  const selected = (selection: SelectionNode) =>
    (selection.directives ?? []).every(({ name, arguments: args }) => {
      const condition = args?.find((arg) => arg.name.value === "if")?.value;
      if (name.value === "include") {
        return holds(condition);
      }
      return name.value !== "skip" || !holds(condition);
    });
  const keys = (selections: readonly SelectionNode[]): string[] =>
    selections.filter(selected).flatMap((selection) => {
      if (selection.kind === Kind.FIELD) {
        return [selection.alias?.value ?? selection.name.value];
      }
      const fragment =
        selection.kind === Kind.INLINE_FRAGMENT
          ? selection
          : document.definitions.find(
              (definition): definition is FragmentDefinitionNode =>
                definition.kind === Kind.FRAGMENT_DEFINITION &&
                definition.name.value === selection.name.value,
            );
      return keys(fragment?.selectionSet.selections ?? []);
    });
  return keys(operation?.selectionSet.selections ?? []);
}

/** Whether a value can be a GraphQL response, or an entry in its `errors`. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The first `errors` entry of the first answer holding any, a batch's array
 * holding an answer per operation. Entries not all objects are read as none.
 */
function firstFailure(body: unknown): GraphqlWireError | undefined {
  const errors =
    [body]
      .flat()
      .map(wireErrors)
      .find((entries) => entries.length > 0) ?? [];
  return errors.every(isRecord) ? errors[0] : undefined;
}

/**
 * A refusal, read the same from its body whatever path it arrived by: its
 * first failure, or else a gateway's `{ code, message }`.
 */
function refusal(body: unknown, status: number, requestId: string | undefined): PlatformApiError {
  const failure = firstFailure(body);
  if (failure) {
    return platformError(failure, status, requestId);
  }
  const payload = (isRecord(body) ? body : {}) as GraphqlWireError & {
    code?: string;
    details?: unknown;
  };
  return platformError(
    { message: payload.message, extensions: { code: payload.code, details: payload.details } },
    status,
    requestId,
  );
}

/**
 * An answer Apollo read no GraphQL from. A 2xx lands here only when its body is
 * not JSON, or holds neither `data` nor `errors`: an object then leaves out the
 * fields asked for, and anything else is no answer at all.
 */
function fromServerError(error: ServerError | ServerParseError): PlatformApiError {
  let body: unknown;
  try {
    body = JSON.parse(error.bodyText) as unknown;
  } catch {
    body = undefined;
  }
  const requestId = requestIdOf(error.response);
  if (error.statusCode < 300) {
    return isRecord(body) ? incompleteAnswer(requestId) : unreadableAnswer(error.response);
  }
  return refusal(body, error.statusCode, requestId);
}

/**
 * Raise an `errors` entry as the platform's own error, an answer that is no
 * object, or whose `errors` is no list of objects, as unreadable, and one leaving out a root
 * field it was asked for as incomplete. A refusal keeps its status. Here rather
 * than in the client, because only here is the response's request id known.
 */
const platformErrors = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    map((result) => {
      const { response } = operation.getContext() as { response?: Response };
      // A refusal declaring the GraphQL response type arrives here parsed.
      const refused = refusedStatus(response);
      if (refused !== undefined) {
        throw refusal(result, refused, requestIdOf(response));
      }
      const errors = wireErrors(result);
      if (!isRecord(result) || !Array.isArray(result.errors ?? []) || !errors.every(isRecord)) {
        throw unreadableAnswer(response);
      }
      const [failure] = errors;
      if (failure) {
        throw platformError(failure, 500, requestIdOf(response));
      }
      const data = result.data as Record<string, unknown> | null | undefined;
      const fields = rootFieldsOf(operation.query, operation.variables);
      if (fields.some((field) => data?.[field] === undefined)) {
        throw incompleteAnswer(requestIdOf(response));
      }
      return result;
    }),
    // With a response in hand, anything else failing is Apollo reading a
    // body that holds no response, or a batch answered at the wrong length.
    // That keeps what it read, which is read as `map` reads an answer.
    catchError((error: unknown) => {
      const { response } = operation.getContext() as { response?: Response };
      const read = ServerError.is(error) || ServerParseError.is(error);
      if (response === undefined || error instanceof PlatformApiError || read) {
        throw error;
      }
      const body = isRecord(error) ? error.result : undefined;
      const refused = refusedStatus(response);
      if (refused !== undefined) {
        throw refusal(body, refused, requestIdOf(response));
      }
      const failure = firstFailure(body);
      if (failure) {
        throw platformError(failure, 500, requestIdOf(response));
      }
      throw unreadableAnswer(response);
    }),
  ),
);

export function createGraphqlTransport(options: GraphqlTransportOptions): ApolloClient {
  const http: HttpLink.Options = {
    uri: options.endpoint,
    credentials: "include",
    fetch: options.fetchImpl,
    headers: Object.fromEntries(new Headers(options.headers)),
  };
  const auth = new SetContextLink(async (context) => {
    const token = await options.getAccessToken();
    return {
      headers: {
        ...(context.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.tenantId ? { [TENANT_HEADER]: options.tenantId } : {}),
      },
    };
  });
  return new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      platformErrors,
      auth,
      ApolloLink.split(
        (operation) => operation.operationType === OperationTypeNode.QUERY,
        new BatchHttpLink({ ...http, batchMax: MAX_BATCH }),
        new HttpLink(http),
      ),
    ]),
  });
}

/**
 * Run one operation and return its data, `__typename` removed, holding every
 * root field it asked for.
 *
 * @throws {PlatformApiError} For every failure the API answers, transport or GraphQL.
 * @throws The error that stopped a request reaching the API, `fetch`'s or the token's.
 */
export async function runOperation(
  client: ApolloClient,
  source: string,
  variables?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  // `gql` caches each parsed document by its text.
  const query = gql(source);
  try {
    const { data } = isMutationOperation(query)
      ? await client.mutate<Record<string, unknown>>({
          mutation: query,
          variables,
          fetchPolicy: "no-cache",
        })
      : await client.query<Record<string, unknown>>({
          query,
          variables,
          fetchPolicy: "no-cache",
        });
    // `platformErrors` refused an answer without its fields, so `data` is set.
    return stripTypename(data ?? {});
  } catch (error) {
    if (ServerError.is(error) || ServerParseError.is(error)) {
      throw fromServerError(error);
    }
    throw error;
  }
}
